import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, TextField, Grid } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchMembersByMemberCategory, updateMember } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function MemberCategoryMemberEditPage() {
  const { businessDomainId, categoryId, memberId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    memberNumber: "",
    memberDescription: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const detailPath = `/strategy/${businessDomainId}/member-categories/${categoryId}/members/${memberId}`;

  const loadData = useCallback(async () => {
    try {
      const members = await fetchMembersByMemberCategory(categoryId);
      const found = (Array.isArray(members) ? members : []).find(
        (m) => String(m.memberId) === String(memberId)
      );
      if (found) {
        setOriginal(found);
        setForm({
          firstName: found.firstName || "",
          lastName: found.lastName || "",
          email: found.email || "",
          password: found.password || "",
          memberNumber: found.memberNumber || "",
          memberDescription: found.memberDescription || "",
        });
      }
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, memberId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const next = {};
    if (!form.firstName.trim()) next.firstName = t("message.error.required");
    if (!form.lastName.trim()) next.lastName = t("message.error.required");
    if (!form.email.trim()) next.email = t("message.error.required");
    if (!form.password.trim()) next.password = t("message.error.required");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await updateMember({
        ...original,
        companyId: COMPANY_ID,
        memberId,
        memberCategoryId: categoryId,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        memberNumber: form.memberNumber,
        memberDescription: form.memberDescription,
      });
      notify.success(t("message.update.success"));
      navigate(detailPath, { replace: true });
    } catch (e) { notify.apiError(e); }
    finally { setSubmitting(false); }
  };

  if (!original) return null;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">{t("member.edit.title")}</Typography>
      </Box>
      <Grid container spacing={2} sx={{ maxWidth: 600 }}>
        <Grid size={12} md={6}>
          <TextField
            label={t("member.lastName")}
            value={form.lastName}
            onChange={handleChange("lastName")}
            error={!!errors.lastName}
            helperText={errors.lastName}
            fullWidth
            required
          />
        </Grid>
        <Grid size={12} md={6}>
          <TextField
            label={t("member.firstName")}
            value={form.firstName}
            onChange={handleChange("firstName")}
            error={!!errors.firstName}
            helperText={errors.firstName}
            fullWidth
            required
          />
        </Grid>
        <Grid size={12}>
          <TextField
            label={t("member.email")}
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            required
          />
        </Grid>
        <Grid size={12}>
          <TextField
            label={t("member.password")}
            type="password"
            value={form.password}
            onChange={handleChange("password")}
            error={!!errors.password}
            helperText={errors.password}
            fullWidth
            required
          />
        </Grid>
        <Grid size={12} md={6}>
          <TextField
            label={t("member.memberNumber")}
            value={form.memberNumber}
            onChange={handleChange("memberNumber")}
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <TextField
            label={t("common.description")}
            value={form.memberDescription}
            onChange={handleChange("memberDescription")}
            multiline
            rows={3}
            fullWidth
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {t("common.save")}
        </Button>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/member-categories/${categoryId}/members/${memberId}`)}>{t("common.cancel")}</Button>
      </Box>
    </Box>
  );
}
